import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {debounceTime, distinctUntilChanged, filter} from 'rxjs';
import { FreemiumBrand } from '../../../shared/types/freemium.types';
import { FreemiumService } from '../../../services/freemium.service';
import { UtilService } from '../../../services/util.service';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';
import {PosthogService} from '../../../services/posthog.service';
import {PosthogEventName} from '../../../services/types';
import {ColoredChipComponent} from '../../../shared/components/colored-chip/colored-chip.component';

@Component({
  selector: 'app-brand-search',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule, AssetVersionPipe, ColoredChipComponent],
  templateUrl: './brand-search.component.html',
  styleUrl: './brand-search.component.scss',
})
export class BrandSearchComponent implements OnInit {
  private freemiumService = inject(FreemiumService);
  private destroyRef = inject(DestroyRef);
  private utilService = inject(UtilService);
  protected readonly contactUsUrl = 'https://browsi.com/demo/?utm_source=InsightHub&utm_medium=free_trial_sign_up&utm_campaign=brand_selection';
  private posthogService = inject(PosthogService)

  @Input() firstName = '';
  @Output() continueToNextStep = new EventEmitter<void>();

  onContinue(): void {
    if (!this.selectedBrand.value) {
      return;
    }

    const brandKey = this.selectedBrand.value.brandKey;
    const trialCompetitorsKeys = this.competitors.map(competitor => competitor.brandKey);

    this.isSubmitting = true;

    this.freemiumService.updateBrands({
      brandKey,
      trialCompetitorsKeys
    })
      .pipe(
        this.utilService.retryWithCondition(() => true),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.continueToNextStep.emit();
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
  }

  private _ngSelect!: NgSelectComponent;
  private _originalClose!: () => void;

  @ViewChild(NgSelectComponent) set ngSelectRef(comp: NgSelectComponent) {
    if (comp && comp !== this._ngSelect) {
      this._ngSelect = comp;
      this._originalClose = comp.close.bind(comp);
      comp.close = () => {
        if (this.isShowingTrending && !this.selectedBrand.value) {
          return;
        }
        this._originalClose();
      };
    }
  }

  get ngSelect(): NgSelectComponent {
    return this._ngSelect;
  }

  searchControl = new FormControl<string | null>(null);
  selectedBrand = new FormControl<FreemiumBrand | null>(null);
  brandItems: FreemiumBrand[] = [];
  trendingBrands: FreemiumBrand[] = [];
  competitors: FreemiumBrand[] = [];
  isLoading = false;
  isLoadingCompetitors = false;
  isSubmitting = false;
  hasSearched = false;
  showNoResults = false;
  isShowingTrending = false;

  ngOnInit(): void {
    this.brandItems = [];

    this.freemiumService.getTrendingBrands(5)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ brands }) => {
          this.trendingBrands = brands || [];
          const term = this.searchControl.value;
          if (!term) {
            this.brandItems = this.trendingBrands;
            this.isShowingTrending = true;
            // Auto-open dropdown to show trending list (until user types).
            Promise.resolve().then(() => {
              if (this.ngSelect && !this.selectedBrand.value && !this.showNoResults && this.brandItems.length > 0) {
                this.ngSelect.open();
              }
            });
          }
        },
        error: () => {
          this.trendingBrands = [];
        }
      });

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((term) => {
        const normalizedTerm = (term ?? '').trim();
        this.isShowingTrending = normalizedTerm.length === 0 && this.trendingBrands.length > 0;

        if(term && term.length > 2) {
          this.isLoading = true;
          this.hasSearched = true;
          this.showNoResults = false;
          this.isShowingTrending = false;
          this.brandItems = [];
          if (this.ngSelect) {
            this.ngSelect.open();
          }
          this.freemiumService.searchBrands(term).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (response) => {
              const { brands } = response;
              this.brandItems = brands;
              this.isLoading = false;
              this.isShowingTrending = false;
              if (this.brandItems.length > 0) {
                if (this.ngSelect) {
                  this.ngSelect.open();
                }
                this.showNoResults = false;
              } else {
                this.showNoResults = true;
              }
            },
            error: () => {
              this.brandItems = [];
              this.isLoading = false;
              this.showNoResults = true;
            }
          });
        } else {
          if (normalizedTerm.length === 0) {
            this.brandItems = this.trendingBrands;
            this.isShowingTrending = this.trendingBrands.length > 0;
            if (this.ngSelect && this.brandItems.length > 0) {
              this.ngSelect.open();
            }
          } else {
            this.brandItems = [];
            this.isShowingTrending = false;
            if (this.ngSelect) {
              this.ngSelect.close();
            }
          }
          this.isLoading = false;
          this.hasSearched = false;
          this.showNoResults = false;
          this.selectedBrand.setValue(null);
        }
      });
    this.searchControl.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        filter(term => !!term && term.length > 2),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(term => {
        this.posthogService.capture(PosthogEventName.BRAND_SEARCHED, { search_query: term });
      });
  }

  compareBrands(brand1: FreemiumBrand | null, brand2: FreemiumBrand | null): boolean {
    if (!brand1 || !brand2) {
      return brand1 === brand2;
    }
    return brand1.brandKey === brand2.brandKey;
  }

  onBrandChange(brand: FreemiumBrand | null): void {
    if (!brand) {
      this.selectedBrand.setValue(null);
      return;
    }

    this.selectedBrand.setValue(brand);
    this.isLoadingCompetitors = true;
    this.competitors = [];

    this.freemiumService.getTopCompetitors(brand.brandKey)
      .pipe(
        this.utilService.retryWithCondition(() => true),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          this.competitors = response.brands || [];
          this.isLoadingCompetitors = false;
        },
        error: () => {
          this.competitors = [];
          this.isLoadingCompetitors = false;
        }
      });
    this.posthogService.capture(PosthogEventName.BRAND_SELECTED, { brand_key: this.selectedBrand.value?.brandKey });
  }

  clearSelectedBrand(): void {
    this.selectedBrand.setValue(null);
    this.competitors = [];
    this.isLoadingCompetitors = false;
    this.searchControl.setValue(null);
    this.hasSearched = false;
    this.showNoResults = false;
    this.brandItems = this.trendingBrands;
    this.isShowingTrending = this.trendingBrands.length > 0;
  }

  onInputFocus(): void {
    if (this.isShowingTrending && this.brandItems.length > 0 && this.ngSelect) {
      setTimeout(() => this.ngSelect.open());
    }
  }



  onIconError(event: Event): void {
    (event.target as HTMLImageElement).src = 'images/no-logo.png';
  }
}
